import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, pageSchema } from "./common.js";

export const TaskBoardColumnIdSchema = z.enum([
	"inbox",
	"triage",
	"doing",
	"waiting",
	"done",
]);
export type TaskBoardColumnId = z.infer<typeof TaskBoardColumnIdSchema>;

export const TaskBoardPrioritySchema = z.enum(["P1", "P2", "P3"]);
export type TaskBoardPriority = z.infer<typeof TaskBoardPrioritySchema>;

/** Future sharing uses EDITOR / VIEWER. Current default is OWNER-only. */
export const TaskBoardPermissionRoleSchema = z.enum(["OWNER", "EDITOR", "VIEWER"]);
export type TaskBoardPermissionRole = z.infer<typeof TaskBoardPermissionRoleSchema>;

export const TaskBoardMessageRoleSchema = z.enum(["USER", "ASSISTANT", "SYSTEM"]);
export type TaskBoardMessageRole = z.infer<typeof TaskBoardMessageRoleSchema>;

export const TaskBoardTaskSchema = z
	.object({
		id: IdSchema,
		tenantId: IdSchema,
		title: z.string().min(1).max(200),
		detail: z.string().max(20000),
		columnId: TaskBoardColumnIdSchema,
		priority: TaskBoardPrioritySchema,
		ownerUserId: IdSchema,
		createdBy: IdSchema,
		createdAt: IsoDateTimeSchema,
		updatedAt: IsoDateTimeSchema,
	})
	.strict();
export type TaskBoardTask = z.infer<typeof TaskBoardTaskSchema>;

export const TaskBoardPermissionSchema = z
	.object({
		id: IdSchema,
		taskId: IdSchema,
		userId: IdSchema,
		role: TaskBoardPermissionRoleSchema,
		createdAt: IsoDateTimeSchema,
	})
	.strict();
export type TaskBoardPermission = z.infer<typeof TaskBoardPermissionSchema>;

export const TaskBoardMessageSchema = z
	.object({
		id: IdSchema,
		taskId: IdSchema,
		role: TaskBoardMessageRoleSchema,
		content: z.string().min(1).max(20000),
		invocationId: IdSchema.nullable(),
		createdBy: IdSchema.nullable(),
		createdAt: IsoDateTimeSchema,
	})
	.strict();
export type TaskBoardMessage = z.infer<typeof TaskBoardMessageSchema>;

export const CreateTaskBoardTaskRequestSchema = z
	.object({
		title: z.string().min(1).max(200),
		detail: z.string().max(20000).default(""),
		columnId: TaskBoardColumnIdSchema.default("inbox"),
		priority: TaskBoardPrioritySchema.default("P2"),
	})
	.strict();
export type CreateTaskBoardTaskRequest = z.infer<typeof CreateTaskBoardTaskRequestSchema>;

export const UpdateTaskBoardTaskRequestSchema = z
	.object({
		title: z.string().min(1).max(200).optional(),
		detail: z.string().max(20000).optional(),
		columnId: TaskBoardColumnIdSchema.optional(),
		priority: TaskBoardPrioritySchema.optional(),
	})
	.strict()
	.refine(
		(value) =>
			value.title !== undefined ||
			value.detail !== undefined ||
			value.columnId !== undefined ||
			value.priority !== undefined,
		{ message: "at least one field is required" },
	);
export type UpdateTaskBoardTaskRequest = z.infer<typeof UpdateTaskBoardTaskRequestSchema>;

export const SendTaskBoardChatRequestSchema = z
	.object({
		content: z.string().min(1).max(20000),
	})
	.strict();
export type SendTaskBoardChatRequest = z.infer<typeof SendTaskBoardChatRequestSchema>;

export const SendTaskBoardChatResponseSchema = z
	.object({
		userMessage: TaskBoardMessageSchema,
		assistantMessage: TaskBoardMessageSchema,
	})
	.strict();
export type SendTaskBoardChatResponse = z.infer<typeof SendTaskBoardChatResponseSchema>;

export const TaskBoardTaskPageSchema = pageSchema(TaskBoardTaskSchema);
export const TaskBoardMessagePageSchema = pageSchema(TaskBoardMessageSchema);
